import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

interface PackageUnit {
  id: string;
  componentName: string;
  componentType: string;
  unitPriceCents: number;
}

interface Transaction {
  id: string;
  created_at: string;
  total_cents: number;
  status: string;
  currency: string;
  package_name?: string;
  guide_name?: string;
  installments_total?: number;
  installments_paid?: number;
  next_invoice_at?: string;
  stripe_subscription_schedule_id?: string;
  package_offer_id?: string;
  units?: PackageUnit[];
}

const formatCurrency = (cents: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(cents / 100);
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const getStatusBadge = (status: string) => {
  const statusConfig: Record<string, { label: string; className: string }> = {
    'pending_payment': { label: 'Pending', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
    'paid_pending_delivery': { label: 'Paid', className: 'bg-green-100 text-green-800 border border-green-200' },
    'delivered': { label: 'Delivered', className: 'bg-[#304031]/10 text-[#304031] border border-[#304031]/20' },
    'cancelled': { label: 'Cancelled', className: 'bg-[#2C2731]/10 text-[#2C2731]/70 border border-[#2C2731]/20' },
    'refunded': { label: 'Refunded', className: 'bg-red-100 text-red-800 border border-red-200' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-[#2C2731]/10 text-[#2C2731]/70 border border-[#2C2731]/20' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

export const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTransaction, setExpandedTransaction] = useState<string | null>(null);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const supabase = getSupabaseClient();

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Not authenticated');
      }

      // Fetch orders for this user with package details
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          created_at,
          total_cents,
          status,
          currency,
          installments_total,
          installments_paid,
          next_invoice_at,
          stripe_subscription_schedule_id,
          package_offer_id,
          package_offers!orders_package_offer_id_fkey (
            name,
            package_offer_units (
              id,
              component_name,
              component_type,
              unit_price_cents
            )
          ),
          profiles!orders_guide_id_fkey (
            full_name
          )
        `)
        .eq('seeker_id', user.id)
        .order('created_at', { ascending: false });

      if (ordersError) {
        throw ordersError;
      }

      const transactionsData: Transaction[] = (orders || []).map((order: any) => ({
        id: order.id,
        created_at: order.created_at,
        total_cents: order.total_cents,
        status: order.status,
        currency: order.currency || 'usd',
        package_name: order.package_offers?.name || 'Package',
        guide_name: order.profiles?.full_name || 'Guide',
        installments_total: order.installments_total,
        installments_paid: order.installments_paid,
        next_invoice_at: order.next_invoice_at,
        stripe_subscription_schedule_id: order.stripe_subscription_schedule_id,
        package_offer_id: order.package_offer_id,
        units: (order.package_offers?.package_offer_units || []).map((unit: any) => ({
          id: unit.id,
          componentName: unit.component_name,
          componentType: unit.component_type,
          unitPriceCents: unit.unit_price_cents,
        })),
      }));

      setTransactions(transactionsData);
    } catch (err) {
      console.error('Failed to load transactions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/profile');
  };

  const toggleExpanded = (transactionId: string) => {
    setExpandedTransaction(expandedTransaction === transactionId ? null : transactionId);
  };

  const calculateFuturePayments = (transaction: Transaction) => {
    if (!transaction.installments_total || !transaction.installments_paid || !transaction.next_invoice_at) {
      return [];
    }

    const remaining = transaction.installments_total - transaction.installments_paid;
    if (remaining <= 0) return [];

    const paymentAmount = transaction.total_cents / transaction.installments_total;
    const futurePayments = [];
    let currentDate = new Date(transaction.next_invoice_at);

    for (let i = 0; i < remaining; i++) {
      futurePayments.push({
        date: new Date(currentDate),
        amount: paymentAmount,
      });
      currentDate = new Date(currentDate.setMonth(currentDate.getMonth() + 1));
    }

    return futurePayments;
  };

  const groupUnitsByType = (units: PackageUnit[]) => {
    const groups = new Map<string, { type: string; name: string; price: number; count: number }>();

    units.forEach((unit) => {
      const key = `${unit.componentType}-${unit.componentName}-${unit.unitPriceCents}`;
      const existing = groups.get(key);

      if (existing) {
        existing.count += 1;
      } else {
        groups.set(key, {
          type: unit.componentType,
          name: unit.componentName,
          price: unit.unitPriceCents,
          count: 1,
        });
      }
    });

    return Array.from(groups.values());
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFCEF] text-[#2C2731]">
        <header className="bg-[#2C2731] text-[#FFFCEF] shadow-sm">
          <div className="max-w-sm mx-auto px-4 py-3 flex items-center justify-between">
            <button
              className="rounded-full p-2 border border-[#FFFCEF]/30 text-[#FFFCEF] hover:bg-[#FFFCEF]/10 transition"
              onClick={handleBack}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-semibold">Transactions</span>
            <div className="w-6" />
          </div>
        </header>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#2C2731]" />
            <p className="text-[#2C2731]/80">Loading transactions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFCEF] text-[#2C2731]">
        <header className="bg-[#2C2731] text-[#FFFCEF] shadow-sm">
          <div className="max-w-sm mx-auto px-4 py-3 flex items-center justify-between">
            <button
              className="rounded-full p-2 border border-[#FFFCEF]/30 text-[#FFFCEF] hover:bg-[#FFFCEF]/10 transition"
              onClick={handleBack}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="font-semibold">Transactions</span>
            <div className="w-6" />
          </div>
        </header>
        <div className="max-w-sm mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 w-full rounded-lg bg-[#2C2731] py-2 text-sm font-semibold text-[#FFFCEF]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCEF] text-[#2C2731]">
      <header className="bg-[#2C2731] text-[#FFFCEF] shadow-sm">
        <div className="max-w-sm mx-auto px-4 py-3 flex items-center justify-between">
          <button
            className="rounded-full p-2 border border-[#FFFCEF]/30 text-[#FFFCEF] hover:bg-[#FFFCEF]/10 transition"
            onClick={handleBack}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-semibold">Transactions</span>
          <div className="w-6" />
        </div>
      </header>

      <div className="max-w-sm mx-auto px-4 py-6">
        {transactions.length === 0 ? (
          <div className="bg-white rounded-lg p-8 text-center shadow-sm border border-[#2C2731]/10">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFFCEF] border border-[#2C2731]/20">
              <svg className="h-8 w-8 text-[#2C2731]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-[#2C2731] mb-2">No Transactions Yet</h3>
            <p className="text-sm text-[#2C2731]/80">
              Your payment history and receipts will appear here once you make a purchase.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {transactions.map((transaction) => {
              const isExpanded = expandedTransaction === transaction.id;
              const futurePayments = calculateFuturePayments(transaction);
              const groupedUnits = groupUnitsByType(transaction.units || []);
              const hasInstallments = transaction.installments_total && transaction.installments_total > 1;

              return (
                <div key={transaction.id} className="bg-white/60 rounded-xl border border-[#2C2731]/20 overflow-hidden shadow-sm">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2C2731]">{transaction.package_name}</h3>
                        <p className="text-sm text-[#2C2731]/70 mt-1">With {transaction.guide_name}</p>
                        <p className="text-xs text-[#2C2731]/60 mt-1">{formatDate(transaction.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#2C2731]">{formatCurrency(transaction.total_cents, transaction.currency)}</p>
                        {hasInstallments && (
                          <p className="text-xs text-[#2C2731]/60 mt-1">
                            {formatCurrency(transaction.total_cents / transaction.installments_total!, transaction.currency)}/mo
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      {getStatusBadge(transaction.status)}

                      {hasInstallments && (
                        <div className="text-xs text-[#2C2731]/70">
                          {transaction.installments_paid || 0} of {transaction.installments_total} payments made
                        </div>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-[#2C2731]/20 space-y-4">
                        {/* Package Content */}
                        {groupedUnits.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-[#2C2731] mb-2">Package Contents</h4>
                            <div className="space-y-2">
                              {groupedUnits.map((group, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex-1">
                                    <p className="text-[#2C2731]">{group.name}</p>
                                    <p className="text-xs text-[#2C2731]/60 capitalize">{group.type}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-[#2C2731]/80">
                                      {group.count > 1 && <span className="text-[#2C2731]/60">{group.count}x </span>}
                                      {formatCurrency(group.price, transaction.currency)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Future Payments Schedule */}
                        {futurePayments.length > 0 && (
                          <div>
                            <h4 className="text-sm font-semibold text-[#2C2731] mb-2">Upcoming Payments</h4>
                            <div className="space-y-2">
                              {futurePayments.map((payment, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 w-2 rounded-full bg-[#304031]"></div>
                                    <p className="text-[#2C2731]/80">{formatDate(payment.date.toISOString())}</p>
                                  </div>
                                  <p className="text-[#2C2731] font-medium">
                                    {formatCurrency(payment.amount, transaction.currency)}
                                  </p>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-[#2C2731]/10">
                              <div className="flex items-center justify-between text-sm">
                                <p className="text-[#2C2731]/70">Remaining Balance</p>
                                <p className="font-semibold text-[#2C2731]">
                                  {formatCurrency(
                                    futurePayments.reduce((sum, p) => sum + p.amount, 0),
                                    transaction.currency
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Transaction Details */}
                        <div>
                          <h4 className="text-sm font-semibold text-[#2C2731] mb-2">Transaction Details</h4>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#2C2731]/70">Transaction ID</span>
                              <span className="text-[#2C2731] font-mono text-xs">{transaction.id.slice(0, 8)}...</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#2C2731]/70">Purchase Date</span>
                              <span className="text-[#2C2731]">{formatDate(transaction.created_at)}</span>
                            </div>
                            {hasInstallments && transaction.next_invoice_at && (
                              <div className="flex justify-between">
                                <span className="text-[#2C2731]/70">Next Payment</span>
                                <span className="text-[#2C2731]">{formatDate(transaction.next_invoice_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-[#FDF1C3] px-4 py-3 border-t border-[#2C2731]/20 flex items-center justify-between">
                    <button
                      onClick={() => toggleExpanded(transaction.id)}
                      className="text-sm font-medium text-[#2C2731] hover:text-[#1F1924] flex items-center gap-1 transition"
                    >
                      {isExpanded ? (
                        <>
                          <span>Show Less</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </>
                      ) : (
                        <>
                          <span>View Details</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </button>
                    <button className="text-sm font-medium text-[#304031] hover:text-[#214D3C] transition">
                      View Receipt
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionsPage;
